import { NgIf } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { firstValueFrom } from 'rxjs'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import { BaseComponent, FeatureType, Grid, ListItem, ModalSize, resolveCanonicalListValue, VehicleInspection, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { VehicleInspectionModalComponent } from './vehicle-inspection-modal.component'

export interface InspectableVehicle {
    equipmentCode: string
    description: string
    mainCategory: string
    operatorHrRef: number | null
    hourReading: number | null
}

@Component({
    selector: 'app-vehicle-inspection-list',
    templateUrl: './vehicle-inspection-list.component.html',
    standalone: true,
    imports: [
        FormsModule,
        NgIf,
        FaIconComponent,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class VehicleInspectionListComponent extends BaseComponent {
    private route = inject(ActivatedRoute)
    private router = inject(Router)
    private destroyRef = inject(DestroyRef)

    /** Set briefly so the modal can scroll to a checklist row (e.g. deep link from email / dashboard). */
    focusInspectionItemId: string | null = null

    private pendingOpenInspectionId: string | null = null
    private pendingOpenInspectionItemId: string | null = null

    faXmark = faXmark
    grid: Grid<VehicleInspection>
    activeUsers: VpUser[] = []
    vehicleList: InspectableVehicle[] = []
    vehicleMap: ListItem<string>[] = []
    userMap: ListItem<number>[] = []

    /** All users (including inactive) used to resolve display names in the grid. */
    private userNameByHrRef = new Map<number, string>()

    constructor() {
        super()
        this.grid = new Grid<VehicleInspection>(this.services)
            .setupModalEditing(
                this.services.data.vehicleInspections,
                'Vehicle Inspection',
                VehicleInspectionModalComponent,
                () => VehicleInspection.createNew(),
                this,
                ModalSize.extraLarge,
            )
            .filterBy(item => [item.vehicleDisplay, item.vehicleAssignedToName, item.inspectorName, item.vehicleMainCategory, item.status])
            .wrapColumnHeaders()

        this.grid.modified.on(this.refreshItems.bind(this))

        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.captureOpenIntentFromRoute()
            if (this.pendingOpenInspectionId && this.grid.items?.length) {
                queueMicrotask(() => this.tryOpenInspectionFromPendingIntent())
            }
        })

        this.refresh()
    }

    refresh() {
        this.captureOpenIntentFromRoute()

        this.services.data
            .load(
                true,
                true,
                // Active users drive the assignment dropdowns; all users resolve names for the grid (incl. inactive).
                this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                this.services.data.get<InspectableVehicle[]>('api/VehicleInspection/GetInspectableVehicles'),
                this.services.data.vehicleInspections.queryAll(),
            )
            .subscribe({
                next: result => {
                    this.applyLookupData(result)
                    const items = result[3] as VehicleInspection[]
                    this.assignUserNames(items)
                    this.grid.items = items
                    this.initialLoadingComplete()
                    this.tryOpenInspectionFromPendingIntent()
                },
                error: () => {
                    this.initialLoadingComplete()
                },
            })
    }

    private loadLookupData() {
        return this.services.data.load(
            true,
            true,
            this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
            this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
            this.services.data.get<InspectableVehicle[]>('api/VehicleInspection/GetInspectableVehicles'),
        )
    }

    private applyLookupData(result: unknown[]) {
        this.activeUsers = result[0] as VpUser[]
        const allUsers = result[1] as VpUser[]
        this.vehicleList = result[2] as InspectableVehicle[]
        this.userMap = this.activeUsers.map(u => new ListItem(u.id, u.name))
        this.userNameByHrRef = new Map<number, string>(allUsers.map(u => [u.id, u.name]))
        this.vehicleMap = this.vehicleList.map(v => new ListItem(v.equipmentCode, `${v.equipmentCode} — ${v.description}`))
    }

    private ensureVehicleListLoaded(): Promise<void> {
        if (this.vehicleList.length > 0 && this.userMap.length > 0) {
            return Promise.resolve()
        }
        return firstValueFrom(this.loadLookupData()).then(result => {
            this.applyLookupData(result)
        })
    }

    openAddForEquipment(equipmentCode: string): Promise<any> | null {
        if (!this.canEdit) {
            return null
        }
        return this.ensureVehicleListLoaded().then(() => {
            const canonicalCode = resolveCanonicalListValue(equipmentCode, this.vehicleList.map(v => v.equipmentCode)) ?? equipmentCode
            const vehicle = this.vehicleList.find(v => v.equipmentCode === canonicalCode)
            const vi = VehicleInspection.createNew()
            vi.vehicleEquipmentCode = canonicalCode
            vi.vehicleDescription = vehicle?.description ?? ''
            vi.vehicleMainCategory = vehicle?.mainCategory ?? ''
            vi.vehicleAssignedToHrRef = vehicle?.operatorHrRef ?? null
            if (vi.vehicleAssignedToHrRef != null) {
                vi.vehicleAssignedToName = this.userNameByHrRef.get(vi.vehicleAssignedToHrRef) ?? ''
            }
            if (vehicle?.hourReading != null && vehicle.hourReading > 0) {
                vi.hourReading = vehicle.hourReading
            }
            return this.grid.addItemWith(vi)
        })
    }

    private captureOpenIntentFromRoute() {
        const inspectionId = this.route.snapshot.queryParamMap.get('openInspectionId')
        if (!inspectionId) return
        this.pendingOpenInspectionId = inspectionId
        this.pendingOpenInspectionItemId = this.route.snapshot.queryParamMap.get('openInspectionItemId')
    }

    private tryOpenInspectionFromPendingIntent() {
        const inspectionId = this.pendingOpenInspectionId
        if (!inspectionId) return

        const itemId = this.pendingOpenInspectionItemId
        this.pendingOpenInspectionId = null
        this.pendingOpenInspectionItemId = null

        const clearParams = () => {
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { openInspectionId: null, openInspectionItemId: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            })
        }

        const row = this.grid.items.find(v => String(v.id).toLowerCase() === String(inspectionId).toLowerCase())
        if (!row) {
            this.services.notification.warning('Vehicle Inspection not found')
            clearParams()
            return
        }

        if (itemId) this.focusInspectionItemId = String(itemId).toLowerCase()
        this.grid.selectedItem = row
        this.grid.editItem(row)
        this.focusInspectionItemId = null
        clearParams()
    }

    private refreshItems() {
        this.services.data.vehicleInspections.queryAll().subscribe({
            next: items => {
                this.assignUserNames(items)
                this.grid.items = items
            },
        })
    }

    /**
     * The model can't resolve user names on its own (no vpUsers repo); the list is the one place
     * we have the user maps, so populate inspector / assigned-to display names here before the grid
     * renders. Uses the all-users map so names still show even if the user is no longer active.
     */
    private assignUserNames(items: VehicleInspection[]) {
        for (const i of items) {
            i.inspectorName = i.inspectorHrRef == null ? '' : this.userNameByHrRef.get(i.inspectorHrRef) ?? ''
            i.vehicleAssignedToName = i.vehicleAssignedToHrRef == null ? '' : this.userNameByHrRef.get(i.vehicleAssignedToHrRef) ?? ''
        }
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.SafetyVehicleInspectionEdit])
    }

    deleteSelectedItem() {
        const selected = this.grid.selectedItem
        if (!selected) return
        this.services.modals.confirm('Delete', 'Are you sure you want to delete this Vehicle Inspection?').then(() => {
            this.services.data.vehicleInspections.delete(selected).subscribe({
                next: () => this.refreshItems(),
            })
        })
    }
}
